import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { getAuthRoleNames } from "../services/authService";
import {
  ADMIN_ROLES,
  OPERATIONAL_ROLES,
  SDR_WEB_ROLES,
  STUDIO_WEB_ROLES,
} from "../Routes/roleAccess";
import usePlanFeatures from "../hooks/usePlanFeatures";
import { PlanFeatureKey } from "../services/sessionCapabilitiesService";

/** Longest-prefix wins so /products/valuation stays under catalog, not a phantom leaf. */
const MENU_PATH_RULES: { id: string; prefixes: string[] }[] = [
  { id: "dashboard", prefixes: ["/dashboard"] },
  { id: "agenda", prefixes: ["/agenda"] },
  { id: "reviews", prefixes: ["/reviews"] },
  { id: "orders", prefixes: ["/orders"] },
  { id: "clients", prefixes: ["/clients"] },
  { id: "crm", prefixes: ["/crm"] },
  { id: "counter_lookup", prefixes: ["/consulta"] },
  { id: "team", prefixes: ["/professionals", "/units", "/users"] },
  {
    id: "catalog",
    prefixes: [
      "/services",
      "/service-bundles",
      "/service-packages",
      "/loyalty-cards",
      "/client-subscription-plans",
      "/products",
    ],
  },

  { id: "financial", prefixes: ["/financial"] },
  { id: "marketing", prefixes: ["/marketing"] },
  { id: "settings", prefixes: ["/settings"] },
];

export const resolveActiveMenuId = (pathname: string): string | null => {
  let best: { id: string; len: number } | null = null;

  for (const rule of MENU_PATH_RULES) {
    for (const prefix of rule.prefixes) {
      if (pathname === prefix || pathname.startsWith(`${prefix}/`)) {
        if (!best || prefix.length > best.len) {
          best = { id: rule.id, len: prefix.length };
        }
      }
    }
  }

  return best?.id ?? null;
};

export type MenuItem = {
  id?: string;
  label?: string;
  isHeader?: boolean;
  icon?: string;
  link?: string;
  stateVariables?: boolean;
  click?: (e: React.MouseEvent<HTMLElement>) => void;
  subItems?: MenuItem[];
  parentId?: string;
  allowedRoles?: string[];
  /** When set, item is soft-locked (Premium badge) if the plan lacks the feature. */
  requiredPlanFeature?: PlanFeatureKey | string;
  badgeName?: string;
  badgeColor?: string;
  planLocked?: boolean;
};

const MENU_ITEM_ROLES: Record<string, string[]> = {
  dashboard: SDR_WEB_ROLES,
  agenda: SDR_WEB_ROLES,
  reviews: SDR_WEB_ROLES,
  orders: STUDIO_WEB_ROLES,
  clients: SDR_WEB_ROLES,
  crm: STUDIO_WEB_ROLES,
  counter_lookup: ["receptionist"],
  team: ADMIN_ROLES,
  professionals: STUDIO_WEB_ROLES,
  units: ADMIN_ROLES,
  users: ADMIN_ROLES,
  catalog: ADMIN_ROLES,
  services: ADMIN_ROLES,
  service_bundles: ADMIN_ROLES,
  service_packages: ADMIN_ROLES,
  client_subscription_plans: ADMIN_ROLES,
  products: ADMIN_ROLES,
  products_valuation: ADMIN_ROLES,
  financial: ADMIN_ROLES,
  expenses: ADMIN_ROLES,
  dre: ADMIN_ROLES,
  day_close: ADMIN_ROLES,
  marketing: ADMIN_ROLES,
  marketing_contact_ruler: ADMIN_ROLES,
  marketing_email_campaigns: ADMIN_ROLES,
  settings: ADMIN_ROLES,
  settings_general: ADMIN_ROLES,
  settings_visual_lab: ADMIN_ROLES,
  settings_team_permissions: ADMIN_ROLES,
  settings_integrations: ADMIN_ROLES,
  anamnesis_templates: ADMIN_ROLES,
};

/** Menu id → plan feature key (doc 10 § 3.0.2). Items without mapping stay visible. */
const MENU_ITEM_PLAN_FEATURES: Record<string, PlanFeatureKey> = {
  products: "inventory",
  products_valuation: "inventory",
  financial: "expenses_dre",
  expenses: "expenses_dre",
  dre: "expenses_dre",
  day_close: "expenses_dre",
  settings_integrations: "integrations_app_store",
  settings_messaging: "whatsapp_lite",
  anamnesis_templates: "anamnesis",
  crm: "crm",
};

const itemAllowedForRoles = (item: MenuItem, userRoles: string[]): boolean => {
  if (!item.id) {
    return true;
  }

  const allowedRoles = item.allowedRoles ?? MENU_ITEM_ROLES[item.id] ?? OPERATIONAL_ROLES;

  return allowedRoles.some((role) => userRoles.includes(role));
};

const itemAllowedForPlan = (
  item: MenuItem,
  can: (feature: PlanFeatureKey | string) => boolean
): boolean => {
  const feature =
    item.requiredPlanFeature ??
    (item.id ? MENU_ITEM_PLAN_FEATURES[item.id] : undefined);

  if (!feature) {
    return true;
  }

  return can(feature);
};

export const filterMenuByRoles = (
  menuItems: MenuItem[],
  userRoles: string[]
): MenuItem[] => {
  const filteredItems = menuItems
    .map((item) => {
      if (item.subItems?.length) {
        const visibleSubItems = item.subItems.filter((subItem) =>
          itemAllowedForRoles(subItem, userRoles)
        );

        if (visibleSubItems.length === 0) {
          return null;
        }

        return { ...item, subItems: visibleSubItems };
      }

      if (item.isHeader) {
        return item;
      }

      return itemAllowedForRoles(item, userRoles) ? item : null;
    })
    .filter((item): item is MenuItem => item !== null);

  return pruneEmptyHeaders(filteredItems);
};

export const filterMenuByPlanFeatures = (
  menuItems: MenuItem[],
  can: (feature: PlanFeatureKey | string) => boolean,
  premiumBadgeLabel = "Premium"
): MenuItem[] => {
  const annotate = (item: MenuItem): MenuItem => {
    if (!itemAllowedForPlan(item, can)) {
      return {
        ...item,
        planLocked: true,
        badgeName: premiumBadgeLabel,
        badgeColor: "warning",
      };
    }

    return item;
  };

  const annotated = menuItems.map((item) => {
    if (item.subItems?.length) {
      const parent = annotate(item);
      const subItems = item.subItems.map(annotate);

      return { ...parent, subItems };
    }

    if (item.isHeader) {
      return item;
    }

    return annotate(item);
  });

  return pruneEmptyHeaders(annotated);
};

const pruneEmptyHeaders = (filteredItems: MenuItem[]): MenuItem[] => {
  const result: MenuItem[] = [];

  filteredItems.forEach((item, index) => {
    if (!item.isHeader) {
      result.push(item);
      return;
    }

    const nextHeaderIndex = filteredItems.findIndex(
      (candidate, candidateIndex) => candidateIndex > index && candidate.isHeader
    );
    const sectionItems = filteredItems.slice(
      index + 1,
      nextHeaderIndex === -1 ? filteredItems.length : nextHeaderIndex
    );

    if (sectionItems.some((sectionItem) => !sectionItem.isHeader)) {
      result.push(item);
    }
  });

  return result;
};

function updateIconSidebar(e: React.MouseEvent<HTMLElement>) {
  const target = e.target as HTMLElement;
  const anchor = target.closest("a");

  if (anchor && anchor.getAttribute("sub-items")) {
    const ul = document.getElementById("two-column-menu");
    const iconItems = ul?.querySelectorAll(".nav-icon.active");
    const activeIconItems = iconItems ? Array.from(iconItems) : [];

    activeIconItems.forEach((item) => {
      item.classList.remove("active");
      const id = item.getAttribute("sub-items");
      const panel = id ? (document.getElementById(id) as HTMLElement | null) : null;

      if (panel) {
        panel.classList.remove("show");
      }
    });
  }
}

const MOBILE_BREAKPOINT = 768;

const Navdata = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeMenuId, setActiveMenuId] = useState<string | null>(() =>
    resolveActiveMenuId(location.pathname)
  );
  const { can, isLoading: planFeaturesLoading } = usePlanFeatures();
  const premiumBadgeLabel = t("plan.menu.premiumBadge");

  useEffect(() => {
    const fromPath = resolveActiveMenuId(location.pathname);
    if (fromPath) {
      setActiveMenuId(fromPath);
    }
  }, [location.pathname]);

  useEffect(() => {
    const isMobile =
      typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT;

    if (isMobile) {
      document.body.classList.remove("twocolumn-panel");
      return;
    }

    if (activeMenuId) {
      document.body.classList.remove("twocolumn-panel");
      return;
    }

    document.body.classList.add("twocolumn-panel");
  }, [activeMenuId]);

  const menuItems = useMemo(() => {
    const makeSubmenuProps = (menuId: string, link: string) => ({
      link,
      stateVariables: activeMenuId === menuId,
      click: (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        setActiveMenuId(menuId);
        updateIconSidebar(e);
        if (link) {
          navigate(link);
        }
      },
    });

    /** Single-route modules still open the 2nd column with a named panel. */
    const makeLeafModule = (
      menuId: string,
      label: string,
      icon: string,
      link: string
    ): MenuItem => ({
      id: menuId,
      label,
      icon,
      ...makeSubmenuProps(menuId, link),
      subItems: [
        {
          id: `${menuId}_home`,
          label,
          link,
          parentId: menuId,
          // Inherit RBAC of the parent — `_home` is not in MENU_ITEM_ROLES.
          allowedRoles: MENU_ITEM_ROLES[menuId],
        },
      ],
    });

    const rawMenuItems: MenuItem[] = [
      makeLeafModule("dashboard", "menu.dashboard", "ri-dashboard-2-line", "/dashboard"),
      makeLeafModule("agenda", "menu.agenda", "ri-calendar-2-line", "/agenda"),
      makeLeafModule("reviews", "menu.reviews", "ri-star-smile-line", "/reviews"),
      makeLeafModule("orders", "menu.orders", "ri-file-list-3-line", "/orders"),
      makeLeafModule("clients", "menu.clients", "ri-user-heart-line", "/clients"),
      makeLeafModule("crm", "menu.crm", "ri-flow-chart", "/crm"),
      makeLeafModule(
        "counter_lookup",
        "menu.counterLookup",
        "ri-search-eye-line",
        "/consulta"
      ),
      {
        id: "team",
        label: "menu.team",
        icon: "ri-team-line",
        ...makeSubmenuProps("team", "/professionals"),
        subItems: [
          {
            id: "professionals",
            label: "menu.team.professionals",
            link: "/professionals",
            parentId: "team",
          },
          {
            id: "units",
            label: "menu.team.units",
            link: "/units",
            parentId: "team",
          },
          {
            id: "users",
            label: "menu.team.users",
            link: "/users",
            parentId: "team",
          },
        ],
      },
      {
        id: "catalog",
        label: "menu.catalog",
        icon: "ri-stack-line",
        ...makeSubmenuProps("catalog", "/services"),
        subItems: [
          {
            id: "services",
            label: "menu.catalog.services",
            link: "/services",
            parentId: "catalog",
          },
          {
            id: "service_bundles",
            label: "menu.catalog.bundles",
            link: "/service-bundles",
            parentId: "catalog",
          },
          {
            id: "service_packages",
            label: "menu.catalog.packages",
            link: "/service-packages",
            parentId: "catalog",
          },
          {
            id: "loyalty_cards",
            label: "menu.catalog.loyaltyCards",
            link: "/loyalty-cards",
            parentId: "catalog",
          },
          {
            id: "client_subscription_plans",
            label: "menu.catalog.clubs",
            link: "/client-subscription-plans",
            parentId: "catalog",
          },

          {
            id: "products",
            label: "menu.catalog.products",
            link: "/products",
            parentId: "catalog",
          },
          {
            id: "products_valuation",
            label: "menu.catalog.productsValuation",
            link: "/products/valuation",
            parentId: "catalog",
          },
        ],
      },
      {
        id: "financial",
        label: "menu.financial",
        icon: "ri-wallet-3-line",
        ...makeSubmenuProps("financial", "/financial/expenses"),
        subItems: [
          {
            id: "expenses",
            label: "menu.financial.expenses",
            link: "/financial/expenses",
            parentId: "financial",
          },
          {
            id: "dre",
            label: "menu.financial.dre",
            link: "/financial/dre",
            parentId: "financial",
          },
          {
            id: "day_close",
            label: "menu.financial.dayClose",
            link: "/financial/day-close",
            parentId: "financial",
          },
        ],
      },
      {
        id: "marketing",
        label: "menu.marketing",
        icon: "ri-mail-send-line",
        ...makeSubmenuProps("marketing", "/marketing/contact-ruler"),
        subItems: [
          {
            id: "marketing_contact_ruler",
            label: "menu.marketing.contactRuler",
            link: "/marketing/contact-ruler",
            parentId: "marketing",
          },
          {
            id: "marketing_email_campaigns",
            label: "menu.marketing.emailCampaigns",
            link: "/marketing/email-campaigns",
            parentId: "marketing",
          },
        ],
      },

      {
        id: "settings",
        label: "menu.settings",
        icon: "ri-settings-4-line",
        ...makeSubmenuProps("settings", "/settings/general"),
        subItems: [
          {
            id: "settings_general",
            label: "menu.settings.general",
            link: "/settings/general",
            parentId: "settings",
          },
          {
            id: "settings_visual_lab",
            label: "menu.settings.visual_lab",
            link: "/settings/visual-lab",
            parentId: "settings",
          },
          {
            id: "settings_operation",
            label: "menu.settings.operation",
            link: "/settings/operation",
            parentId: "settings",
          },
          {
            id: "settings_payments",
            label: "menu.settings.payments",
            link: "/settings/payments",
            parentId: "settings",
          },
          {
            id: "settings_email_notifications",
            label: "menu.settings.email_notifications",
            link: "/settings/email-notifications",
            parentId: "settings",
          },
          {
            id: "settings_messaging",
            label: "menu.settings.messaging",
            link: "/settings/messaging",
            parentId: "settings",
          },
          {
            id: "settings_whatsapp_extension",
            label: "menu.settings.whatsapp_extension",
            link: "/settings/whatsapp-extension",
            parentId: "settings",
          },
          {
            id: "settings_team_permissions",
            label: "menu.settings.team_permissions",
            link: "/settings/team-permissions",
            parentId: "settings",
          },
          {
            id: "settings_calendar_exceptions",
            label: "menu.settings.calendar_exceptions",
            link: "/settings/calendar-exceptions",
            parentId: "settings",
          },
          {
            id: "settings_integrations",
            label: "menu.settings.integrations",
            link: "/settings/integrations",
            parentId: "settings",
          },
          {
            id: "anamnesis_templates",
            label: "menu.settings.anamnesis_templates",
            link: "/settings/anamnesis-templates",
            parentId: "settings",
          },
          {
            id: "settings_client_tags",
            label: "menu.settings.client_tags",
            link: "/settings/client-tags",
            parentId: "settings",
          },
          {
            id: "settings_clinical_map_regions",
            label: "menu.settings.clinical_map_regions",
            link: "/settings/clinical-map-regions",
            parentId: "settings",
          },
          {
            id: "settings_clinical_checklist_templates",
            label: "menu.settings.clinical_checklist_templates",
            link: "/settings/clinical-checklist-templates",
            parentId: "settings",
          },
        ],
      },
    ];

    const byRole = filterMenuByRoles(rawMenuItems, getAuthRoleNames());

    if (planFeaturesLoading) {
      return byRole as any[];
    }

    return filterMenuByPlanFeatures(byRole, can, premiumBadgeLabel) as any[];
  }, [activeMenuId, can, navigate, planFeaturesLoading, premiumBadgeLabel]);

  return <React.Fragment>{menuItems}</React.Fragment>;
};

export default Navdata;
